import React, { useCallback, useEffect, useRef } from "react";

import { IDictionary } from "@Components/shared/types";
import { isNil, usePrevious } from "@Utils";
import JSONEditor, { JSONEditorOptions } from "jsoneditor";
import editorThemes from "./editorThemes";

import "ace-builds/src-min-noconflict/mode-json";
import "jsoneditor/dist/jsoneditor.css";

import "./editor.scss";

/**
 * Options for the JSON editor component.
 * @extends JSONEditorOptions
 */
interface IJSONEditorOptions extends JSONEditorOptions {
    /**
     * This is in the docs but not in the types
     */
    showErrorTable: boolean;

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLElement/style) */
    style?: CSSStyleDeclaration;
}

/**
 * Props for the JSON editor component.
 * @extends JSONEditorOptions
 */
export interface IEditorProps extends Pick<JSONEditorOptions, "onError" | "onModeChange" | "mode" | "schema"> {
    /**
     * Set a callback function triggered when the contents of the JSONEditor change.
     * Will only be triggered on changes made by the user, not in case of programmatic changes via the functions.
     */
    onChange?: (value: IDictionary) => void;

    /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/HTMLElement/style) */
    style?: CSSStyleDeclaration;

    /**
     * The theme of the editor
     */
    theme?: (typeof editorThemes)[number];
    /**
     * The JSON value to display/edit.
     */
    value?: IDictionary;
}

/**
 * JSON editor component.
 * @param props - The component props.
 * @returns The rendered JSON editor component.
 */
const Editor = React.forwardRef<JSONEditor, IEditorProps>((props, ref) => {
    const {
        onChange,
        onError,
        onModeChange,

        mode = "code",
        schema,
        style,
        theme = "dreamweaver",
        value,
    } = props;

    const htmlElementRef = useRef<HTMLDivElement>(null);
    const jsonEditorRef = useRef<JSONEditor | null>(null);
    const previousTheme = usePrevious(theme);

    function isDictionary(value: unknown): value is IDictionary {
        return value !== null && typeof value === "object" && !Array.isArray(value);
    }

    /**
     * Handles changes in the JSON editor.
     */
    const handleChange = useCallback(() => {
        if (onChange) {
            try {
                const text = jsonEditorRef.current?.getText();
                if (text === "") {
                    onChange({});
                    return;
                }

                const currentJson: unknown = jsonEditorRef.current?.get();
                if (!isDictionary(currentJson)) {
                    throw new Error("Invalid JSON");
                }

                if (value !== currentJson) {
                    onChange(currentJson);
                }
            } catch (error) {
                const err =
                    error instanceof Error ? error : new Error("An error occurred while parsing the JSON string.");

                if (onError) {
                    onError(err);
                }
            }
        }
    }, [onChange, onError, value]);

    const createEditor = useCallback(() => {
        // dynamically import the theme
        require(`ace-builds/src-min-noconflict/theme-${theme}`);

        const options: IJSONEditorOptions = {
            onChange: handleChange,
            onError,
            onModeChange,

            enableSort: false,
            enableTransform: false,
            history: ["tree", "form", "preview"].includes(mode),
            mode,
            modes: ["code", "tree"],
            name: "root",
            showErrorTable: true,
            schema,
            style,
            theme: `ace/theme/${theme}`,
        };

        if (isNil(htmlElementRef.current)) return;

        jsonEditorRef.current = new JSONEditor(htmlElementRef.current, options);

        // all the parent component to interact with the editor
        if (!isNil(ref) && "current" in ref) {
            ref.current = jsonEditorRef.current;
        }
    }, [handleChange, mode, onError, onModeChange, ref, schema, style, theme]);

    // dynamically and update the theme
    useEffect(() => {
        if (!jsonEditorRef.current || isNil(theme) || theme === previousTheme) {
            return;
        }

        const currentValue: string = jsonEditorRef.current.getText();

        jsonEditorRef.current.destroy();

        createEditor();

        jsonEditorRef.current.setText(currentValue);
    }, [createEditor, previousTheme, theme]);

    // validate schema
    useEffect(() => {
        if (!jsonEditorRef.current || isNil(schema)) {
            return;
        }

        jsonEditorRef.current.setSchema(schema);
    }, [schema]);

    // create new editor on mount
    useEffect(() => {
        if (jsonEditorRef.current) {
            return;
        }

        createEditor();
    }, [createEditor]);

    // destroy when unmount
    useEffect(() => {
        return () => {
            if (jsonEditorRef.current) {
                jsonEditorRef.current.destroy();
                jsonEditorRef.current = null;
            }
        };
    }, []);

    return <div className="json-editor" ref={htmlElementRef} />;
});

export default Editor;
